import { DateUtils, PersonDoc, UserDoc, UserForm, UserSession } from 'common';
import { User } from '../models/User';
import { FetchInterceptor } from './FetchInterceptor';
import { ConcourseDb } from '../ConcourseDb';
import { getDeviceInfoUtil } from '../utils/deviceUtils';

export function setRedirectPath(path) {
  localStorage.setItem('concourse_redirect_after_login', path);
}

export function getRedirectPath() {
  return localStorage.getItem('concourse_redirect_after_login');
}

export class AuthService {
  /**
   * @param email
   * @param password
   * @returns Can we please type the return object so devs know what to expect from this?
   */
  static async login(email: string, password: string): Promise<void> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/auth/login';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, deviceInfo: getDeviceInfoUtil() }),
    });

    const data = await response.json();

    if (response.status === 401) {
      throw new Error(data.message);
    }

    this.setSessionData(data);
  }

  /**
   * @param email
   * @param password
   * @returns Can we please type the return object so devs know what to expect from this?
   */
  static async signUp(formData: UserForm): Promise<UserSession> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/auth/sign-up';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (response.status === 401) {
      throw new Error((await response.json()).message);
    }
    const sessionData: UserSession = await response.json();
    AuthService.setSessionData(sessionData);
    return sessionData;
  }

  static async requestResetPassword(email: string) {
    const url = `${globalThis.ccLambdaBaseUrl}/fn/auth/send-reset-password-email`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message);
    }
  }

  static async requestAccessCode(): Promise<UserSession> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/request-access-code', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + globalThis.user.token,
      },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message);
    return data;
  }

  static async validateAccessCode(email: string, code: string, password: string = null): Promise<UserSession> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/validate-access-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, password }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message);
    this.setSessionData(data as UserSession);
    return data;
  }

  static async updateUser(doc: UserDoc): Promise<UserSession> {
    const response = await authFetch(globalThis.ccLambdaBaseUrl + '/fn/auth/update-user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + globalThis.user.token,
      },
      body: JSON.stringify({ formData: doc }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error('Error updating user: ' + data.message);

    this.setSessionData(data);
    return data;
  }

  static async refreshToken(): Promise<UserSession> {
    const userId: string = globalThis.user.doc._id;
    const refreshToken: string = globalThis.refreshToken;
    const url = `${globalThis.ccLambdaBaseUrl}/fn/auth/refresh`;
    const refreshResponse = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, refreshToken }),
    });
    const data = await refreshResponse.json();
    if (!refreshResponse.ok) {
      throw new Error(data.message || 'Failed to refresh token');
    } else if (data.token) {
      this.setSessionData(data);
      return data;
    } else {
      console.error('Failed to refresh token', data);
      throw new Error('No token received from refresh endpoint');
    }
  }

  static generateCodeVerifier() {
    const array = new Uint32Array(56);
    crypto.getRandomValues(array);
    return Array.from(array, dec => ('0' + dec.toString(16)).slice(-2)).join('');
  }

  static async generateCodeChallenge(verifier: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(hash)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  static async getMicrosoftParams() {
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    localStorage.setItem('pkce_code_verifier', codeVerifier);
    return new URLSearchParams({
      client_id: `${globalThis.msClientId}`,
      response_type: 'code',
      redirect_uri: `${globalThis.ccFrontEndUrl}/microsoft-callback`,
      response_mode: 'query',
      scope: 'openid profile email',
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      state: 'xyz123',
    });
  }

  static async logout(): Promise<void> {
    // Call server logout endpoint to invalidate refresh token
    if (globalThis.user && globalThis.refreshToken) {
      try {
        await fetch(`${globalThis.ccLambdaBaseUrl}/fn/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${globalThis.couchdbToken}`,
          },
          body: JSON.stringify({ refreshToken: globalThis.refreshToken }),
        });
      } catch (error) {
        console.warn('Failed to call logout endpoint:', error);
        // Continue with local logout even if server call fails
      }
    }

    const localStorageKey = 'user-session';
    localStorage.removeItem(localStorageKey);
    globalThis.user = undefined;
    globalThis.couchdbToken = undefined;
    globalThis.refreshToken = undefined;

    const dbNames = Object.keys(ConcourseDb.instances);
    dbNames.forEach(dbName => {
      console.log(`Stopping replication of ${dbName}`);
      ConcourseDb.instances[dbName].stopReplication();
    });
  }

  /**
   * Retrieves the guest session for a given email, project ID, and link ID.
   * Guest sessions are used by people who have scanned the QR code of a shared link.
   *
   * @param userDoc - The user's document.
   * @param projectId - The ID of the project.
   * @param sharedLinkId - The ID of the link.
   *
   * @return {Promise} A promise that resolves to the guest session.
   */
  static async getGuestSession(userDoc: { email: string; first_name: string; last_name: string }, projectId: string, sharedLinkId: string) {
    const url = globalThis.ccLambdaBaseUrl + '/fn/auth/guest-login';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userDoc, projectId, sharedLinkId }),
    });
    if (!response.ok) {
      throw new Error((await response.json())?.message);
    }

    return await response.json();
  }

  /**
   * Session data is an object with a user, a token, and a refresh token
   * @param sessionData The session data to store in the globalThis object
   */
  static setSessionData(sessionData: UserSession): void {
    console.log(`setSessionData(sessionData:${sessionData.user})`);
    if (!sessionData.user) throw new Error('User does not exist on session'); // user should be sent to login screen, not just throw an error
    globalThis.user = new User(sessionData.user);
    globalThis.couchdbToken = sessionData.token;
    globalThis.refreshToken = sessionData.refreshToken;
    globalThis.company = sessionData.company;

    const localStorageKey = 'user-session';
    localStorage.setItem(localStorageKey, JSON.stringify(sessionData));
  }

  static async generateShareLink(projectId: string, hash: string, roles: string[], expirationDate: string): Promise<{ sharedLinkId: string }> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/auth/generate-shared-link';
    const response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        projectId,
        hash,
        roles,
        expirationDate
      }),
    });
    const responseJson = await response.json();
    if (!response.ok && responseJson.message) {
      throw new Error(responseJson.message);
    }
    return responseJson;
  }

  static async validateSharedLinkId(projectId: string, sharedLinkId: string, refreshToken: string): Promise<string> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/auth/validate-shared-link';
    const response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ projectId, sharedLinkId, refreshToken }),
    });

    const data = await response.json();
    if (!response.ok && data.message) {
      throw new Error(data.message);
    }

    this.setSessionData(data);

    return data.hash;
  }

  static async requestAccess(projectId: string, page: string, role: string, userForm: UserForm): Promise<void> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/auth/request-access';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({
        projectId: projectId,
        requestedRole: role,
        requestedPage: page,
        formData: userForm,
      }),
    });
    const data = await response.json();
    if (!response.ok && data.message) {
      throw new Error(data.message);
    }
  }

  static async adminConfirmAccess(projectId: string, personId: string, roles: string[]): Promise<{ message: string, person: PersonDoc }> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/auth/admin-confirm-access';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({
        projectId: projectId,
        personId: personId,
        roles: roles,
        date: DateUtils.currentIso,
      }),
    });
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.message);
    }
    return json;
  }

  static async deleteAccount(): Promise<string> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/auth/delete-account';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({
        userId: globalThis.user.doc._id,
      }),
    });
    const json = await response.json();
    if (!response.ok) {
      return json.message;
    }
    return null;
  }
}



export function updateSessionDataFromGlobalUser() {
  if (!globalThis.user.doc) throw new Error('User does not exist on globalThis');
  const localStorageKey = 'user-session';
  const sessionData = JSON.parse(localStorage.getItem(localStorageKey));
  sessionData.user = globalThis.user.doc;
  localStorage.setItem(localStorageKey, JSON.stringify(sessionData));
}

export function getAccessToken() {
  return globalThis.couchdbToken;
}

export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

export function setCookie(cname, cvalue, exMins) {
  var d = new Date();
  d.setTime(d.getTime() + exMins * 60 * 1000);
  var expires = 'expires=' + d.toUTCString();
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
}

export function deleteCookie(cname) {
  document.cookie = cname + '=; Max-Age=-99999999;';
}

export const authFetch: typeof fetch = FetchInterceptor.getDefaultInstance().authFetch.bind(FetchInterceptor.getDefaultInstance());
export const authInterceptor: FetchInterceptor = FetchInterceptor.getDefaultInstance();
